import type { PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import type { ValueOf } from 'src/helper/shared-types';
import {
  createEligibleVendorsState,
  createPreferredVendorsState,
  type InitialStateType,
  isVendorEligibleForSub,
  type PropertyListItem,
  type PVCategoryDetailState,
  type PVDetailSubCategory,
  type PVEligibleVendor,
  type PVVendor,
  propertiesInitialState,
} from 'src/redux/properties/types';

const moveInArray = <Item>(list: Item[], fromIndex: number, toIndex: number) => {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return;
  const [moved] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, moved);
};

const findSub = (detail: PVCategoryDetailState | null, subcategoryId: string) =>
  detail?.subCategories.find((sub) => sub.subcategory_id === subcategoryId) ?? null;

// Persist the open category's live preview into its list row before `detail` is dropped.
const syncListPreview = (state: InitialStateType) => {
  const { detail, overriddenCategories } = state.preferredVendors;
  if (!detail) return;
  const listItem = overriddenCategories.find((item) => item.id === detail.category_id);
  if (listItem) listItem.previewNames = detail.defaultVendors.map((vendor) => vendor.name);
};

// Resolve the selected vendor from the eligible map; drop the market flag and
// skills since stored preferences are plain PVVendors.
const findEligibleVendor = (
  vendorsById: Record<string, PVEligibleVendor>,
  vendorId: string,
): PVVendor | null => {
  const vendor = vendorsById[vendorId];
  return vendor ? { vendor_id: vendor.vendor_id, name: vendor.name } : null;
};

type CategoryPayload = { categoryId: string };
type ReorderPayload = { fromIndex: number; toIndex: number };
type VendorPayload = { vendorId: string };
type SubCategoryPayload = { subcategoryId: string };
type SubCategoryVendorPayload = SubCategoryPayload & VendorPayload;
type SubCategoryReorderPayload = SubCategoryPayload & ReorderPayload;
type CategoryAddedPayload = CategoryPayload & { name: string; defaultVendors: PVVendor[] };

const reducers = {
  clearPropertyListData: (state: InitialStateType) => {
    state.data = propertiesInitialState.data;
    state.pagination = propertiesInitialState.pagination;
    state.lastSyncedAt = propertiesInitialState.lastSyncedAt;
    state.marketAssignmentFilter = propertiesInitialState.marketAssignmentFilter;
  },

  setMarketAssignmentFilter: (state: InitialStateType, action: PayloadAction<string | null>) => {
    state.marketAssignmentFilter = action.payload;
  },

  updatePropertyDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      propertyUuid: string;
      data: PropertyListItem | ValueOf<PropertyListItem>;
      key: string;
    }>,
  ) => {
    const propertyUuid = action?.payload?.propertyUuid;
    if (!propertyUuid) {
      return;
    }

    const currentSource = { ...state?.source };
    const isReplaceMode = isEmpty(action?.payload?.key);
    const incomingData = action?.payload?.data;

    if (isReplaceMode) {
      const isPropertyObject =
        Boolean(incomingData) && typeof incomingData === 'object' && !Array.isArray(incomingData);
      if (!isPropertyObject) {
        return;
      }
      state.source = {
        ...currentSource,
        [propertyUuid]: incomingData as PropertyListItem,
      };
    } else {
      const existingProperty = state?.source?.[propertyUuid];
      if (!existingProperty) {
        return;
      }
      const updatedProperty = {
        ...existingProperty,
        [action.payload.key]: incomingData,
      };
      state.source = {
        ...currentSource,
        [propertyUuid]: updatedProperty as PropertyListItem,
      };
    }
  },

  changePropertyDetailsTab: (state: InitialStateType, action: PayloadAction<string>) => {
    if (state.currentPropertyDetailsTab !== action.payload) {
      state.currentPropertyDetailsTab = action.payload;
    }
  },

  // ── Preferred Vendors (property detail tab) ────────────────────────────────
  // Optimistic edits all act on the single open `detail` — there is never more
  // than one category's detail in state, so no category lookup is needed.
  selectPreferredVendorCategory: (
    state: InitialStateType,
    action: PayloadAction<CategoryPayload>,
  ) => {
    const { categoryId } = action.payload;
    const preferredVendors = state.preferredVendors;
    // Re-selecting the open category is a no-op (else eligibleVendors clears
    // without the selectedCategoryId-keyed effect refetching it).
    if (preferredVendors.selectedCategoryId === categoryId) return;
    preferredVendors.selectedCategoryId = categoryId;
    preferredVendors.detailFailed = false;
    // Drop stale detail so the pane shows a skeleton until the new fetch lands.
    if (preferredVendors.detail && preferredVendors.detail.category_id !== categoryId) {
      syncListPreview(state); // persist the outgoing category's edited preview
      preferredVendors.detail = null;
    }
    // The category effect refetches eligible vendors for the new category.
    preferredVendors.eligibleVendors = createEligibleVendorsState();
  },

  // ── Category default vendors (optimistic) ──────────────────────────────────
  reorderDefaultVendors: (state: InitialStateType, action: PayloadAction<ReorderPayload>) => {
    const { detail } = state.preferredVendors;
    if (detail)
      moveInArray(detail.defaultVendors, action.payload.fromIndex, action.payload.toIndex);
  },

  addDefaultVendor: (state: InitialStateType, action: PayloadAction<VendorPayload>) => {
    const { detail, eligibleVendors } = state.preferredVendors;
    const vendor = findEligibleVendor(eligibleVendors.vendorsById, action.payload.vendorId);
    if (
      detail &&
      vendor &&
      !detail.defaultVendors.some((item) => item.vendor_id === vendor.vendor_id)
    ) {
      detail.defaultVendors.push(vendor);
    }
  },

  removeDefaultVendor: (state: InitialStateType, action: PayloadAction<VendorPayload>) => {
    const { detail } = state.preferredVendors;
    if (detail) {
      detail.defaultVendors = detail.defaultVendors.filter(
        (item) => item.vendor_id !== action.payload.vendorId,
      );
    }
  },

  // ── Sub-category vendors (optimistic) ──────────────────────────────────────
  reorderSubVendors: (
    state: InitialStateType,
    action: PayloadAction<SubCategoryReorderPayload>,
  ) => {
    const sub = findSub(state.preferredVendors.detail, action.payload.subcategoryId);
    if (sub) moveInArray(sub.vendors, action.payload.fromIndex, action.payload.toIndex);
  },

  addSubVendor: (state: InitialStateType, action: PayloadAction<SubCategoryVendorPayload>) => {
    const { detail, eligibleVendors } = state.preferredVendors;
    const sub = findSub(detail, action.payload.subcategoryId);
    const vendor = findEligibleVendor(eligibleVendors.vendorsById, action.payload.vendorId);
    if (sub && vendor && !sub.vendors.some((item) => item.vendor_id === vendor.vendor_id)) {
      sub.vendors.push(vendor);
    }
  },

  removeSubVendor: (state: InitialStateType, action: PayloadAction<SubCategoryVendorPayload>) => {
    const sub = findSub(state.preferredVendors.detail, action.payload.subcategoryId);
    if (sub) sub.vendors = sub.vendors.filter((item) => item.vendor_id !== action.payload.vendorId);
  },

  // Begin customizing a sub-category: seed from the category default vendors,
  // filtered to the ones eligible for this sub (what the inheriting row displays).
  customizeSubCategory: (state: InitialStateType, action: PayloadAction<SubCategoryPayload>) => {
    const { detail, eligibleVendors } = state.preferredVendors;
    const sub = findSub(detail, action.payload.subcategoryId);
    if (!detail || !sub) return;
    sub.is_override = true;
    sub.vendors = detail.defaultVendors
      .filter((vendor) =>
        isVendorEligibleForSub(eligibleVendors.vendorsById[vendor.vendor_id], sub.subcategory_id),
      )
      .map((vendor) => ({ vendor_id: vendor.vendor_id, name: vendor.name }));
  },

  // Reset to inheriting. Clearing vendors is enough — the selector re-derives the
  // inheriting display from the default (eligible map is loaded by now).
  resetSubCategory: (state: InitialStateType, action: PayloadAction<SubCategoryPayload>) => {
    const sub = findSub(state.preferredVendors.detail, action.payload.subcategoryId);
    if (sub) {
      sub.is_override = false;
      sub.vendors = [];
    }
  },

  // ── Optimistic rollback (partial-snapshot strategy) ────────────────────────
  // Each mutation snapshots only the slice it touches so a concurrent rollback
  // for one sub-category never clobbers an independent change to another.
  restoreDefaultVendors: (state: InitialStateType, action: PayloadAction<PVVendor[]>) => {
    const { detail } = state.preferredVendors;
    if (detail) detail.defaultVendors = action.payload;
  },

  restoreSubVendors: (
    state: InitialStateType,
    action: PayloadAction<{ subcategoryId: string; vendors: PVVendor[] }>,
  ) => {
    const sub = findSub(state.preferredVendors.detail, action.payload.subcategoryId);
    if (sub) sub.vendors = action.payload.vendors;
  },

  restoreSubCategory: (state: InitialStateType, action: PayloadAction<PVDetailSubCategory>) => {
    const { detail } = state.preferredVendors;
    if (!detail) return;
    const index = detail.subCategories.findIndex(
      (sub) => sub.subcategory_id === action.payload.subcategory_id,
    );
    if (index !== -1) detail.subCategories[index] = action.payload;
  },

  clearPreferredVendors: (state: InitialStateType) => {
    state.preferredVendors = createPreferredVendorsState();
  },

  // ── Real-time category sync (Pusher) ────────────────────────────────────────
  // Mirrors another client's category override add/remove into the list.
  // Never touches the open `detail` pane — the acting client already owns that
  // via the addOverrideCategory/removeOverrideCategory thunks.
  addPreferredVendorCategory: (
    state: InitialStateType,
    action: PayloadAction<CategoryAddedPayload>,
  ) => {
    const { categoryId, name, defaultVendors } = action.payload;
    const preferredVendors = state.preferredVendors;
    if (preferredVendors.overriddenCategories.some((item) => item.id === categoryId)) return;

    preferredVendors.overriddenCategories.push({
      id: categoryId,
      name,
      previewNames: defaultVendors.map((vendor) => vendor.name),
    });
  },

  removePreferredVendorCategory: (
    state: InitialStateType,
    action: PayloadAction<CategoryPayload>,
  ) => {
    const { categoryId } = action.payload;
    const preferredVendors = state.preferredVendors;
    const removed = preferredVendors.overriddenCategories.find((item) => item.id === categoryId);
    if (!removed) return;

    preferredVendors.overriddenCategories = preferredVendors.overriddenCategories.filter(
      (item) => item.id !== categoryId,
    );

    if (preferredVendors.selectedCategoryId === categoryId) {
      preferredVendors.selectedCategoryId = preferredVendors.overriddenCategories[0]?.id ?? null;
      preferredVendors.detail = null;
      // Drop the removed category's eligible vendors so detail refetches clean.
      preferredVendors.eligibleVendors = createEligibleVendorsState();
    }
  },

  // Re-sync the header badge count with the live override list after any add/remove.
  syncOverriddenVendorsCount: (
    state: InitialStateType,
    action: PayloadAction<{ propertyId?: string }>,
  ) => {
    const { propertyId } = action.payload;
    if (!propertyId) return;
    const details = state.detailsSource?.[propertyId];
    if (details) {
      details.overridden_vendors_count = state.preferredVendors.overriddenCategories.length;
    }
  },
};

export default reducers;
